import { PERMISSIONS } from "./permissions";

type PermissionValue = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const PLATFORM_TELEMETRY_PERMISSIONS: PermissionValue[] = [
  PERMISSIONS.CONFIG_READ,
  PERMISSIONS.CONFIG_FEATURE_FLAGS_MANAGE,
];

export function normalizePermissionList(permissions: readonly string[] | Set<string> | null | undefined): Set<string> {
  if (!permissions) return new Set();
  return permissions instanceof Set ? permissions : new Set(permissions.filter(Boolean));
}

export function hasPermission(
  permissions: readonly string[] | Set<string> | null | undefined,
  permission: string,
): boolean {
  return normalizePermissionList(permissions).has(permission);
}

export function hasAnyPermission(
  permissions: readonly string[] | Set<string> | null | undefined,
  required: readonly string[],
): boolean {
  const permissionSet = normalizePermissionList(permissions);
  return required.some((permission) => permissionSet.has(permission));
}

export function canViewPlatformTelemetry(permissions: readonly string[] | Set<string> | null | undefined): boolean {
  return hasAnyPermission(permissions, PLATFORM_TELEMETRY_PERMISSIONS);
}

export function permissionsForPath(pathname: string): readonly string[] {
  const path = pathname.split("?")[0].split("#")[0];

  if (path === "/needlepath" || path === "/mnemo") return PLATFORM_TELEMETRY_PERMISSIONS;
  if (path === "/config") return [PERMISSIONS.CONFIG_FEATURE_FLAGS_MANAGE];
  if (path === "/config/branding") return [PERMISSIONS.CONFIG_BRANDING_MANAGE];
  if (path === "/key-manager") return [PERMISSIONS.CONFIG_KEYS_MANAGE];
  if (path === "/audit-logs") return [PERMISSIONS.AUDIT_READ];
  if (path === "/users" || /^\/users\/\d+\/roles$/.test(path)) return [PERMISSIONS.USERS_READ];
  if (path === "/add-user") return [PERMISSIONS.USERS_MANAGE];
  if (path === "/roles") return [PERMISSIONS.ROLES_READ];

  if (path === "/agent/new" || path === "/agent/create" || path === "/agent/conversational") {
    return [PERMISSIONS.AGENTS_CREATE];
  }
  if (/^\/agent\/[^/]+\/chatAgent$/.test(path)) return [PERMISSIONS.AGENTS_RUN];
  if (path === "/agents" || /^\/agent\/[^/]+$/.test(path)) return [PERMISSIONS.AGENTS_READ];

  if (path === "/tools" || /^\/tool\/[^/]+$/.test(path)) return [PERMISSIONS.TOOLS_READ];
  if (path === "/tool-forge") return [PERMISSIONS.TOOLS_CREATE, PERMISSIONS.TOOLS_CONFIGURE];

  if (path === "/kb" || /^\/kb\/[^/]+\/[^/]+$/.test(path)) return [PERMISSIONS.KNOWLEDGE_READ];
  if (path === "/kb/code-indexer" || path === "/kb/document-indexer") return [PERMISSIONS.KNOWLEDGE_CREATE];

  if (path === "/workflow/generate" || path === "/workflow/intent-build") return [PERMISSIONS.WORKFLOWS_CREATE];
  if (/^\/workflow\/[^/]+\/intent-edit$/.test(path) || /^\/workflow\/[^/]+\/edit$/.test(path)) {
    return [PERMISSIONS.WORKFLOWS_UPDATE];
  }
  if (/^\/workflow\/[^/]+\/run$/.test(path)) return [PERMISSIONS.WORKFLOWS_RUN];
  if (path === "/workflow/approvals" || /^\/workflow\/approvals\/[^/]+$/.test(path)) {
    return [PERMISSIONS.WORKFLOWS_APPROVE];
  }
  if (path === "/workflows" || /^\/workflow\/[^/]+$/.test(path)) return [PERMISSIONS.WORKFLOWS_READ];

  return [];
}

export function canAccessPath(
  pathname: string,
  permissions: readonly string[] | Set<string> | null | undefined,
): boolean {
  const required = permissionsForPath(pathname);
  return required.length === 0 || hasAnyPermission(permissions, required);
}
